import React, { useEffect, useRef, useState } from 'react';
import { ArrowUp } from 'lucide-react';

// easeOutQuint — длинный, мягкий, кинематографичный замедляющий хвост
const easeOutQuint = (t) => 1 - Math.pow(1 - t, 5);

export default function BackToTop() {
 const [visible, setVisible] = useState(false);
 const rafRef = useRef(null);

 useEffect(() => {
  const onScroll = () => setVisible(window.scrollY > 400);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
  return () => {
   window.removeEventListener('scroll', onScroll);
   if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };
 }, []);

 const cancelScroll = () => {
  if (rafRef.current) {
   cancelAnimationFrame(rafRef.current);
   rafRef.current = null;
  }
 };

 const handleClick = () => {
  cancelScroll();
  // Respect reduced-motion users
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
   window.scrollTo(0, 0);
   return;
  }

  const startY = window.scrollY || window.pageYOffset;
  if (startY <= 0) return;

  // Calm cinematic timing: ~0.9s minimum, up to ~2.2s for very long pages
  const duration = Math.min(2200, Math.max(900, Math.round(startY * 0.55)));
  const startTime = performance.now();

  // User-initiated wheel / touch / keys cancel the animation immediately
  const onUserInterrupt = (e) => {
   if (e.type === 'keydown' && !['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(e.key)) return;
   cancelScroll();
   detach();
  };
  const detach = () => {
   window.removeEventListener('wheel', onUserInterrupt, { passive: true });
   window.removeEventListener('touchstart', onUserInterrupt, { passive: true });
   window.removeEventListener('keydown', onUserInterrupt);
  };
  window.addEventListener('wheel', onUserInterrupt, { passive: true });
  window.addEventListener('touchstart', onUserInterrupt, { passive: true });
  window.addEventListener('keydown', onUserInterrupt);

  const step = (now) => {
   const elapsed = now - startTime;
   const t = Math.min(1, elapsed / duration);
   const y = startY * (1 - easeOutQuint(t));
   window.scrollTo(0, y);
   if (t < 1) {
    rafRef.current = requestAnimationFrame(step);
   } else {
    rafRef.current = null;
    detach();
   }
  };
  rafRef.current = requestAnimationFrame(step);
 };

 return (
  <button
   type="button"
   onClick={handleClick}
   aria-label="Наверх"
   data-testid="back-to-top-btn"
   className={`group fixed bottom-6 left-6 z-[45] flex items-center gap-3 px-4 h-11 border border-[#B38F6F]/40 text-[#B38F6F] bg-[#0a0a0a]/55 backdrop-blur-md transition-all duration-[600ms] ease-out hover:bg-[#B38F6F] hover:text-[#0d0d0d] hover:border-[#B38F6F] ${
    visible ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'
   }`}
  >
   <ArrowUp className="w-3.5 h-3.5 transition-transform duration-500 group-hover:-translate-y-0.5" strokeWidth={1.25} />
   <span className="text-[10px] uppercase tracking-[0.32em] font-light">Наверх</span>
  </button>
 );
}
